import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Stack,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Snackbar,
} from "@mui/material";
import { useLoginCheck } from "../../hooks/useLoginCheck";
import { lineConsole } from "../../utils/lineConsoleClient";
import ScenarioMindMap from "../../components/ScenarioMindMap";
import "../../styles/scss/scenarioManager.scss";

const MENU_PROPS = { className: "segment-broadcast-menu" };
// 新規作成ダイアログを開くための番兵（きっかけはダイアログの中で選ぶ）
const ADD_PICK = "__pick__";

/**
 * 画面用の言い換え。
 *
 * API(meta) が返すラベルは実装寄りの用語（「アンカー日 + オフセット日数」など）なので、
 * **配信担当者が読んで分かる日本語**に置き換える。値そのものは API と同じ。
 */
const FIRE_METHOD_LABELS = {
  immediate: "すぐに送る（申し込み・入会の直後）",
  fixed: "決まった日の決まった時刻に送る",
  later_of: "レッスンが終わってから送る",
  monthly: "毎月決まった日に送る",
  trial_day: "体験レッスン当日に送る",
};

const CONFLICT_LABELS = {
  defer: "翌日にまわす（後で必ず届ける）",
  drop: "送るのをやめる（日が変わると意味がなくなる連絡）",
  bypass: "1日1通の上限を超えても送る（急ぎの連絡）",
};

const KIND_LABELS = {
  cta: "本文＋ボタン",
  text: "本文だけ",
  survey: "アンケート（選択肢をタップ）",
};

// アンケートの選択肢の上限（slash_line の scenario_store.SURVEY_MAX_OPTIONS と同じ）
const SURVEY_MAX_OPTIONS = 10;

const CUSTOM_LINK = "__custom__";

/** 新規シナリオの初期値（枝ごとに「そのまま動く」既定を入れる）。 */
function newScenarioDefaults(anchor) {
  const base = {
    kind: "cta",
    enabled: false, // 作った直後に配信されないよう既定は無効
    is_mandatory: false,
    track_cta: true,
    emoji_allowed: true,
    on_conflict: "defer",
    position: 50,
    send_conditions: {},
    bodies: [{ variant_key: "a", weight: 100, body: "", cta_label: "", cta_url_template: "", extra_ctas: [], survey_options: [] }],
  };
  if (anchor === "trial_reminder") {
    return { ...base, category: "pre_trial", trigger: { method: "fixed", offset_days: -1, at_time: "18:00" } };
  }
  if (anchor === "trial_branch") {
    return {
      ...base,
      category: "post_trial",
      on_conflict: "bypass",
      trigger: { method: "later_of", relative_minutes: 30, relative_to: "anchor_end", floor_time: "20:00" },
    };
  }
  return { ...base, category: "nurture", trigger: { method: "fixed", offset_days: 3, at_time: "11:00" } };
}

function ScenarioManagerPage() {
  useLoginCheck();

  const [meta, setMeta] = useState(null);
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // 編集中のシナリオ全体
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null); // 保存完了などの一時表示
  const [createFor, setCreateFor] = useState(null); // 新規作成ダイアログの対象アンカー
  const bodyRefs = useRef({});

  const loadList = useCallback(async () => {
    try {
      const [m, l] = await Promise.all([
        meta ? Promise.resolve({ ...meta }) : lineConsole.scenarioMeta(),
        lineConsole.listScenarios(),
      ]);
      if (!meta) setMeta(m);
      setList(l.scenarios || []);
    } catch (e) {
      setError(`一覧の取得に失敗しました: ${e.message}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const anchorInfo = useMemo(() => {
    const m = {};
    (meta?.anchors || []).forEach((a) => {
      m[a.value] = a;
    });
    return m;
  }, [meta]);

  // A/B（パターン別の本文と配信比率）は廃止。パターンA以外が残っていても編集には出さず、
  // 保存すると bodies が丸ごと置き換わるので、そのとき一緒に消える。
  const onlyPatternA = (d) => {
    const bodies = d.bodies || [];
    const a = bodies.find((b) => b.variant_key === "a") || bodies[0];
    return { ...d, bodies: a ? [{ ...a, variant_key: "a", weight: 100 }] : [] };
  };

  const openScenario = async (sid) => {
    setError(null);
    setNotice(null);
    setWarnings([]);
    setPreview(null);
    try {
      const d = await lineConsole.getScenario(sid);
      setDraft(onlyPatternA(d));
      setSelectedId(sid);
    } catch (e) {
      setError(`取得に失敗しました: ${e.message}`);
    }
  };

  const closeEditor = () => {
    setDraft(null);
    setSelectedId(null);
    setPreview(null);
    setWarnings([]);
  };

  const patchDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const patchTrigger = (patch) =>
    setDraft((d) => ({ ...d, trigger: { ...(d.trigger || {}), ...patch } }));
  const patchBody = (idx, patch) =>
    setDraft((d) => {
      const bodies = d.bodies.map((b, i) => (i === idx ? { ...b, ...patch } : b));
      return { ...d, bodies };
    });

  /** 2つ目以降のボタン（extra_ctas は [ラベル, リンク先] の配列）。 */
  const patchExtraCta = (bodyIdx, ctaIdx, pair) =>
    setDraft((d) => {
      const bodies = d.bodies.map((b, i) => {
        if (i !== bodyIdx) return b;
        const ex = (b.extra_ctas || []).map((x, j) => (j === ctaIdx ? pair : x));
        return { ...b, extra_ctas: ex };
      });
      return { ...d, bodies };
    });

  const addExtraCta = (bodyIdx) =>
    setDraft((d) => {
      const bodies = d.bodies.map((b, i) =>
        i === bodyIdx ? { ...b, extra_ctas: [...(b.extra_ctas || []), ["", ""]] } : b
      );
      return { ...d, bodies };
    });

  const removeExtraCta = (bodyIdx, ctaIdx) =>
    setDraft((d) => {
      const bodies = d.bodies.map((b, i) =>
        i === bodyIdx ? { ...b, extra_ctas: (b.extra_ctas || []).filter((_, j) => j !== ctaIdx) } : b
      );
      return { ...d, bodies };
    });

  /** アンケートの選択肢（survey_options は [ラベル, 回答コード, 返信文, ボタンの文字, ボタンのリンク先]）。
   *  回答コードは集計と返信の紐付けに使うので画面には出さず、追加時に自動で付ける。
   *  文字を変えてもコードは変わらないので、これまでの回答はそのまま数えられる。 */
  const setSurveyOptions = (bodyIdx, fn) =>
    setDraft((d) => {
      const bodies = d.bodies.map((b, i) =>
        i === bodyIdx ? { ...b, survey_options: fn(b.survey_options || []) } : b
      );
      return { ...d, bodies };
    });
  const patchSurveyOption = (bodyIdx, oi, pos, value) =>
    setSurveyOptions(bodyIdx, (opts) =>
      opts.map((o, j) => {
        if (j !== oi) return o;
        const next = [o[0] || "", o[1], o[2] || "", o[3] || "", o[4] || ""];
        next[pos] = value;
        return next;
      })
    );
  const addSurveyOption = (bodyIdx) =>
    setSurveyOptions(bodyIdx, (opts) => [...opts, ["", `o${Date.now().toString(36)}`, "", "", ""]]);
  const removeSurveyOption = (bodyIdx, oi) =>
    setSurveyOptions(bodyIdx, (opts) => opts.filter((_, j) => j !== oi));
  const moveSurveyOption = (bodyIdx, oi, delta) =>
    setSurveyOptions(bodyIdx, (opts) => {
      const to = oi + delta;
      if (to < 0 || to >= opts.length) return opts;
      const next = [...opts];
      [next[oi], next[to]] = [next[to], next[oi]];
      return next;
    });

  /** 送信条件を1項目だけ変える。「指定しない」はキーごと消す（＝無条件）。 */
  const patchCondition = (key, value) =>
    setDraft((d) => {
      const next = { ...(d.send_conditions || {}) };
      if (value === "") delete next[key];
      else next[key] = JSON.parse(value);
      return { ...d, send_conditions: next };
    });

  /** 本文のカーソル位置に差し込み変数を入れる。 */
  const insertPlaceholder = (idx, key) => {
    const el = bodyRefs.current[idx];
    const token = `{${key}}`;
    const cur = draft.bodies[idx].body || "";
    if (!el) {
      patchBody(idx, { body: cur + token });
      return;
    }
    const s = el.selectionStart ?? cur.length;
    const e = el.selectionEnd ?? cur.length;
    patchBody(idx, { body: cur.slice(0, s) + token + cur.slice(e) });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + token.length, s + token.length);
    });
  };

  const save = async () => {
    if (!draft) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    setWarnings([]);
    try {
      const wasEnabled = (list.find((x) => x.scenario_id === draft.scenario_id) || {}).enabled;
      const res = await lineConsole.updateScenario(draft.scenario_id, draft);
      setDraft(onlyPatternA(res.scenario));
      const warns = res.warnings || [];
      setWarnings(warns);
      await loadList();
      // 停止中→配信するに変えて保存したら、その場で対象者を探す
      if (draft.enabled && !wasEnabled) await runNow(draft.scenario_id);
      // 右の編集パネルは開いたままにする。保存のたびに閉じると、続けて直したいときに
      // マップから選び直すことになる（モーダルだった頃の名残）。警告はパネル内に出る。
      if (!warns.length) setToast("保存しました。配信に反映されます。");
    } catch (e) {
      setError(`保存に失敗しました: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  /** 「送信イメージを見る」は**編集中の内容**を描く（保存は不要）。
   *  保存済みの行だけを描いていた頃は、書いた文面が反映されず、
   *  作ったばかりのメッセージでは「(本文なし)」と出ていた。 */
  const doPreview = async () => {
    if (!draft) return;
    setError(null);
    const body = (draft.bodies || [])[0];
    try {
      const p = await lineConsole.previewScenario(draft.scenario_id, "a", {
        body,
        kind: draft.kind,
        track_cta: draft.track_cta,
      });
      setPreview(p);
    } catch (e) {
      setError(`プレビューに失敗しました: ${e.message}`);
    }
  };

  /** 「配信する」を ON にしたら、その場で対象者の判定を走らせる。
   *  通常は毎朝のスキャン待ちで翌日まで何も起きず、効いているか分からないため。 */
  const runNow = async (sid) => {
    try {
      const r = await lineConsole.runScenarioNow(sid);
      if (r && r.reason === "event_driven") {
        setNotice("配信を開始しました。このきっかけは「その出来事が起きた時」に送られます。");
      } else {
        setNotice("配信を開始しました。対象の方を今すぐ探しています（数分以内に届きます）。");
      }
    } catch (e) {
      setNotice("配信を開始しました（対象者の判定は次回の自動チェックで行われます）。");
    }
  };

  const toggleEnabled = async (s) => {
    try {
      const next = !s.enabled;
      await lineConsole.updateScenario(s.scenario_id, { enabled: next });
      await loadList();
      if (draft && draft.scenario_id === s.scenario_id) {
        setDraft((d) => ({ ...d, enabled: next }));
      }
      if (next) await runNow(s.scenario_id);
    } catch (e) {
      setError(`切替に失敗しました: ${e.message}`);
    }
  };

  const doDelete = async () => {
    if (!draft) return;
    if (!window.confirm(`「${draft.name}」を削除します。よろしいですか？（変更履歴から戻せます）`)) return;
    try {
      await lineConsole.deleteScenario(draft.scenario_id);
      setNotice(`${draft.scenario_id} を削除しました。`);
      setDraft(null);
      setSelectedId(null);
      await loadList();
    } catch (e) {
      setError(`削除に失敗しました: ${e.message}`);
    }
  };

  const doCreate = async (payload) => {
    try {
      const res = await lineConsole.createScenario(payload);
      setCreateFor(null);
      setNotice("作成しました。内容を入力して保存し、「配信する」を ON にすると届きはじめます。");
      await loadList();
      await openScenario(res.scenario.scenario_id);
    } catch (e) {
      setError(`作成に失敗しました: ${e.message}`);
    }
  };

  const t = draft?.trigger || {};
  const curAnchor = draft ? anchorInfo[draft.anchor] : null;
  const condFields = (meta?.condition_fields || {})[draft?.anchor] || [];
  // そのきっかけで確実に値が入る差し込み変数だけを出す
  const placeholders = (meta?.placeholders_by_anchor || {})[draft?.anchor] || [];

  // ⚠️ モーダルが開いている間、ページ上部に出しても見えない（実機で保存警告を見落とした）。
  // 開いていればモーダル内に、閉じていればページ上部に出す。
  const feedback = (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarnings([])}>
          <b>保存しましたが、確認してください</b>
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </Alert>
      )}
    </>
  );

  return (
    <Box className="scenario-manager-page" sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        シナリオ管理
      </Typography>
      <Typography variant="body2" className="sm-muted" sx={{ mb: 2 }}>
        自動で届くLINEメッセージの文面・ボタン・送るタイミングを変更できます。保存するとすぐ反映されます。
      </Typography>

      {!draft && feedback}

      {/* 左：マインドマップ／右：編集フォーム。
          ⚠️ 編集はモーダルだったが、閉じるまで全体像が見えず「今どれを触っているか」が
          分からなかった。マップを出したまま右で直せるように並べる。 */}
      <Box className="sm-split">
        <Box className="sm-panel sm-map-panel">
          <ScenarioMindMap
            list={list}
            anchors={meta?.anchors}
            conditionFields={meta?.condition_fields}
            selectedId={selectedId}
            onSelect={openScenario}
            onToggleEnabled={toggleEnabled}
            onAdd={() => setCreateFor(ADD_PICK)}
          />
        </Box>

        <Box className="sm-panel sm-editor-panel">
          {!draft ? (
            <Box className="sm-editor-empty">
              <Typography variant="body2" className="sm-muted">
                左のマップからメッセージを選ぶと、ここで文面・タイミング・送る相手を編集できます。
              </Typography>
            </Box>
          ) : (
            <Box className="sm-editor-body">
              {feedback}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip label={draft.scenario_id} size="small" />
                {draft.is_system && (
                  <Tooltip title="もとから用意されているメッセージです。文面や時刻は変更できます">
                    <Chip label="標準" size="small" />
                  </Tooltip>
                )}
                <Box sx={{ flex: 1 }} />
                <FormControlLabel
                  control={<Switch checked={draft.enabled} onChange={(e) => patchDraft({ enabled: e.target.checked })} />}
                  label={draft.enabled ? "配信する" : "配信を止める"}
                />
              </Stack>

              <TextField label="メッセージの名前（管理用・お客様には見えません）" size="small" fullWidth sx={{ mb: 1 }}
                value={draft.name || ""} onChange={(e) => patchDraft({ name: e.target.value })} />

              {curAnchor && (
                <Typography variant="body2" className="sm-hint" sx={{ mb: 2 }}>
                  送るきっかけ：<b>{curAnchor.label}</b>
                </Typography>
              )}

              {/* ① メッセージの内容 */}
              <SectionTitle>① メッセージの内容</SectionTitle>
              {(draft.bodies || []).map((b, idx) => (
                <Box key={b.variant_key} className="sm-section-body" sx={{ mb: 2 }}>
                  <TextField
                    label="本文" multiline minRows={4} size="small" fullWidth sx={{ mt: 0.5, mb: 0.5 }}
                    value={b.body || ""}
                    inputRef={(el) => { bodyRefs.current[idx] = el; }}
                    onChange={(e) => patchBody(idx, { body: e.target.value })}
                  />
                  <div className="sm-ph-row">
                    {/* ⚠️ きっかけによって入る情報が違う。空欄になる変数は出さない
                        （実機で「入会からの経過」に {lesson_name} を入れて空欄で届いた） */}
                    <span className="sm-muted">差し込み：</span>
                    {placeholders.length === 0 && (
                      <span className="sm-muted">このきっかけでは差し込める情報がありません</span>
                    )}
                    {placeholders.map((p) => (
                      <Tooltip key={p.key} title={`本文に {${p.key}} を入れます（送信時にお客様ごとの${p.label}に変わります）`}>
                        <button type="button" className="sm-ph" onClick={() => insertPlaceholder(idx, p.key)}>
                          {p.label}
                        </button>
                      </Tooltip>
                    ))}
                  </div>

                  {draft.kind === "cta" && (
                    <Stack spacing={2.5} sx={{ mt: 3 }} className="sm-cta-group">
                      <Typography variant="caption" className="sm-muted">ボタン（上から順に表示されます）</Typography>
                      <TextField label="1つ目のボタンの文字（20文字まで）" size="small" fullWidth
                        value={b.cta_label || ""} onChange={(e) => patchBody(idx, { cta_label: e.target.value })} />
                      <LinkTargetField
                        meta={meta}
                        value={b.cta_url_template || ""}
                        onChange={(url, rec) => {
                          patchBody(idx, { cta_url_template: url });
                          // 会員アプリ宛はタップ計測を通すとアプリが開かないので自動で外す（D26）
                          if (rec && typeof rec.track_cta === "boolean") patchDraft({ track_cta: rec.track_cta });
                        }}
                      />

                      {(b.extra_ctas || []).map((pair, ei) => (
                        <Box key={ei} className="sm-extra-cta">
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <TextField label={`${ei + 2}つ目のボタンの文字`} size="small" sx={{ flex: 1 }}
                              value={pair[0] || ""}
                              onChange={(e) => patchExtraCta(idx, ei, [e.target.value, pair[1] || ""])} />
                            <Button size="small" color="error" onClick={() => removeExtraCta(idx, ei)}>削除</Button>
                          </Stack>
                          <LinkTargetField
                            meta={meta}
                            value={pair[1] || ""}
                            onChange={(url) => patchExtraCta(idx, ei, [pair[0] || "", url])}
                          />
                        </Box>
                      ))}
                      {(b.extra_ctas || []).length < 2 && (
                        <Box>
                          <Button size="small" onClick={() => addExtraCta(idx)}>＋ ボタンを追加</Button>
                        </Box>
                      )}
                    </Stack>
                  )}

                  {draft.kind === "survey" && (
                    <Stack spacing={2.5} sx={{ mt: 3 }} className="sm-cta-group">
                      <Typography variant="caption" className="sm-muted">
                        選択肢（上から順に表示されます。最大{SURVEY_MAX_OPTIONS}個）
                        {draft.scenario_id === "3-2" && "。選ばれるとすぐに返信文を送ります"}
                      </Typography>
                      {(b.survey_options || []).map((o, oi) => (
                        <Box key={o[1] || oi} className="sm-extra-cta">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField label={`選択肢${oi + 1}（20文字まで）`} size="small" sx={{ flex: 1 }}
                              inputProps={{ maxLength: 20 }}
                              value={o[0] || ""} onChange={(e) => patchSurveyOption(idx, oi, 0, e.target.value)} />
                            <Button size="small" disabled={oi === 0} onClick={() => moveSurveyOption(idx, oi, -1)}>↑</Button>
                            <Button size="small" disabled={oi === (b.survey_options || []).length - 1}
                              onClick={() => moveSurveyOption(idx, oi, 1)}>↓</Button>
                            <Button size="small" color="error" onClick={() => removeSurveyOption(idx, oi)}>削除</Button>
                          </Stack>
                          {draft.scenario_id === "3-2" && (
                            <>
                              <TextField label="この選択肢を選んだ人への返信" size="small" fullWidth multiline minRows={2}
                                sx={{ mt: 1 }} inputProps={{ maxLength: 500 }}
                                value={o[2] || ""} onChange={(e) => patchSurveyOption(idx, oi, 2, e.target.value)} />
                              <TextField label="返信に付けるボタンの文字（任意・20文字まで）" size="small" fullWidth
                                sx={{ mt: 1 }} inputProps={{ maxLength: 20 }}
                                value={o[3] || ""} onChange={(e) => patchSurveyOption(idx, oi, 3, e.target.value)} />
                              {(o[3] || o[4]) && (
                                <LinkTargetField
                                  meta={meta}
                                  value={o[4] || ""}
                                  onChange={(url) => patchSurveyOption(idx, oi, 4, url)}
                                />
                              )}
                            </>
                          )}
                        </Box>
                      ))}
                      {(b.survey_options || []).length < SURVEY_MAX_OPTIONS && (
                        <Box>
                          <Button size="small" onClick={() => addSurveyOption(idx)}>＋ 選択肢を追加</Button>
                        </Box>
                      )}
                      <Typography variant="caption" className="sm-muted">
                        選択肢の文字を変えても、これまでの回答はその選択肢の回答として数えます。
                        削除した選択肢への過去の回答は、配信分析で「削除した選択肢」として出ます。
                      </Typography>
                    </Stack>
                  )}
                </Box>
              ))}

              {/* ② いつ送るか */}
              <SectionTitle>② いつ送るか</SectionTitle>
              <Box className="sm-section-body">
                <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}>
                  <SelectField label="送るタイミング" value={t.method} width={260}
                    items={(meta?.fire_methods || []).map((m) => ({ value: m.value, label: FIRE_METHOD_LABELS[m.value] || m.label }))}
                    onChange={(v) => patchTrigger({ method: v })} />
                  {t.method === "fixed" && (
                    <>
                      {/* 支払い系は同じきっかけの中で基準日が3種類あるので、どの日から数えるかを選ぶ */}
                      {draft.anchor === "payment_due" && (
                        <SelectField label="何の日から数えるか" value={t.relative_to || "due_date"} width={220}
                          items={meta?.payment_bases}
                          onChange={(v) => patchTrigger({ relative_to: v })} />
                      )}
                      <TextField label="そこから何日後（前は −）" type="number" size="small" sx={{ width: 180 }}
                        value={t.offset_days ?? 0} onChange={(e) => patchTrigger({ offset_days: parseInt(e.target.value || "0", 10) })} />
                      <TextField label="時刻" type="time" size="small" sx={{ width: 130 }} InputLabelProps={{ shrink: true }}
                        value={t.at_time || ""} onChange={(e) => patchTrigger({ at_time: e.target.value })} />
                    </>
                  )}
                  {t.method === "later_of" && (
                    <>
                      <TextField label="終了から何分後" type="number" size="small" sx={{ width: 150 }}
                        value={t.relative_minutes ?? 0} onChange={(e) => patchTrigger({ relative_minutes: parseInt(e.target.value || "0", 10) })} />
                      <TextField label="ただしこの時刻より前には送らない" type="time" size="small" sx={{ width: 220 }} InputLabelProps={{ shrink: true }}
                        value={t.floor_time || ""} onChange={(e) => patchTrigger({ floor_time: e.target.value })} />
                    </>
                  )}
                  {t.method === "monthly" && (
                    <>
                      <TextField label="毎月何日" type="number" size="small" sx={{ width: 120 }}
                        value={t.monthly_day ?? 1} onChange={(e) => patchTrigger({ monthly_day: parseInt(e.target.value || "1", 10) })} />
                      <TextField label="時刻" type="time" size="small" sx={{ width: 130 }} InputLabelProps={{ shrink: true }}
                        value={t.at_time || ""} onChange={(e) => patchTrigger({ at_time: e.target.value })} />
                    </>
                  )}
                  {t.method === "trial_day" && (
                    <TextField label="この時刻より前には送らない" type="time" size="small" sx={{ width: 200 }} InputLabelProps={{ shrink: true }}
                      value={t.floor_time || ""} onChange={(e) => patchTrigger({ floor_time: e.target.value })} />
                  )}
                </Stack>
                <Typography variant="body2" className="sm-summary">
                  {timingSummary(t, draft.anchor, baseLabel(meta, draft, t))}
                </Typography>
              </Box>

              {/* ③ 誰に送るか（条件で分かれる枝だけ） */}
              {condFields.length > 0 && (
                <>
                  <SectionTitle>③ 誰に送るか</SectionTitle>
                  <Box className="sm-section-body">
                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                      {condFields.map((f) => (
                        <SelectField
                          key={f.key} label={f.label} width={260}
                          value={
                            draft.send_conditions && f.key in draft.send_conditions
                              ? JSON.stringify(draft.send_conditions[f.key])
                              : ""
                          }
                          items={[
                            { value: "", label: "指定しない（だれでも）" },
                            ...f.options.map((o) => ({ value: JSON.stringify(o.value), label: o.label })),
                          ]}
                          onChange={(v) => patchCondition(f.key, v)}
                        />
                      ))}
                    </Stack>
                    <Typography variant="body2" className="sm-summary">
                      {conditionSummary(draft.send_conditions, condFields)}
                    </Typography>
                    {curAnchor?.exclusive && (
                      <Typography variant="caption" className="sm-muted">
                        ※ このきっかけでは条件に合う<b>1通だけ</b>が送られます（同じ枝の上にあるものから順に判定）。
                      </Typography>
                    )}
                  </Box>
                </>
              )}

              {/* 詳細設定（普段は触らない） */}
              <Accordion className="sm-adv" disableGutters elevation={0}>
                <AccordionSummary expandIcon={<span className="sm-expand">▼</span>}>
                  <Typography variant="subtitle2">詳細設定（通常は変更しません）</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
                    <SelectField label="メッセージの形" value={draft.kind} width={200}
                      items={(meta?.kinds || []).map((k) => ({ value: k.value, label: KIND_LABELS[k.value] || k.label }))}
                      onChange={(v) => patchDraft({ kind: v })} />
                    <SelectField label="種類（配信の優先度）" value={draft.category} width={200} items={meta?.categories}
                      onChange={(v) => patchDraft({ category: v })} />
                    <SelectField label="1日1通の上限に当たったとき" value={draft.on_conflict} width={300}
                      items={(meta?.conflict_policies || []).map((c) => ({ value: c.value, label: CONFLICT_LABELS[c.value] || c.label }))}
                      onChange={(v) => patchDraft({ on_conflict: v })} />
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
                    <FormControlLabel control={<Switch checked={draft.is_mandatory}
                      onChange={(e) => patchDraft({ is_mandatory: e.target.checked })} />}
                      label="お知らせ停止中の方にも必ず届ける（支払いの連絡など）" />
                    <FormControlLabel control={<Switch checked={draft.track_cta}
                      onChange={(e) => patchDraft({ track_cta: e.target.checked })} />}
                      label="ボタンのタップ数を計測する（会員アプリ・LINE内の予約／入会フォームへのボタンは OFF でも計測されます）" />
                  </Stack>
                  {curAnchor?.exclusive && (
                    <TextField label="判定の順番（小さいほど先に判定）" type="number" size="small" sx={{ width: 220, mt: 2 }}
                      value={draft.position ?? 0} onChange={(e) => patchDraft({ position: parseInt(e.target.value || "0", 10) })} />
                  )}
                </AccordionDetails>
              </Accordion>

              {preview && <LinePreview preview={preview} />}

              <Box className="sm-editor-actions">
                {!draft.is_system && (
                  <Button variant="outlined" color="error" onClick={doDelete}>削除</Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={doPreview}>送信イメージ</Button>
                <Button onClick={closeEditor}>閉じる</Button>
                <Button variant="contained" onClick={save} disabled={loading}>保存する</Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar
        open={!!toast}
        message={toast || ""}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <CreateDialog
        open={createFor === ADD_PICK}
        meta={meta}
        existingIds={list.map((s) => s.scenario_id)}
        onClose={() => setCreateFor(null)}
        onCreate={doCreate}
      />
    </Box>
  );
}

/** 「いつ送るか」を自然文で言い直す（設定ミスに気づけるように）。 */
/** 支払い系の「何の日から数えるか」の表示名。他のきっかけでは空。 */
function baseLabel(meta, draft, t) {
  if (draft.anchor !== "payment_due") return "";
  const found = (meta?.payment_bases || []).find((b) => b.value === (t.relative_to || "due_date"));
  return found ? found.label : "";
}

function timingSummary(t, anchor, base = "") {
  const at = t.at_time || "09:00";
  const origin = base || "きっかけの日";
  switch (t.method) {
    case "immediate":
      return "→ きっかけが起きた直後に、すぐ送ります。";
    case "fixed": {
      const d = t.offset_days ?? 0;
      const when = d === 0 ? "当日" : d > 0 ? `${d}日後` : `${Math.abs(d)}日前`;
      return `→ ${origin}の${when} ${at} に送ります。`;
    }
    case "later_of":
      return `→ レッスン終了の${t.relative_minutes ?? 0}分後に送ります。ただし ${t.floor_time || "20:00"} より前にはなりません。`;
    case "monthly":
      return `→ 毎月${t.monthly_day ?? 1}日の ${at} に送ります（その日を過ぎていれば翌月）。`;
    case "trial_day": {
      const h = (t.params && t.params.early_hours) || 3;
      return `→ 体験レッスンの当日に送ります。午前のレッスンは${h}時間前（${t.floor_time || "09:00"} より前にはなりません）。`;
    }
    default:
      return anchor ? "" : "";
  }
}

/** 送信条件を自然文に。 */
function conditionSummary(conds, fields) {
  const parts = [];
  fields.forEach((f) => {
    if (!conds || !(f.key in conds)) return;
    const opt = f.options.find((o) => JSON.stringify(o.value) === JSON.stringify(conds[f.key]));
    if (opt) parts.push(opt.label);
  });
  if (!parts.length) return "→ 条件なし（このきっかけに当てはまる方すべて）に送ります。";
  return `→ ${parts.join(" / ")} に送ります。`;
}

/** ボタンのリンク先＝候補から選ぶ。候補外なら「その他」で自由入力。 */
function LinkTargetField({ meta, value, onChange }) {
  const targets = meta?.link_targets || [];
  const known = targets.find((t) => t.value === value);
  const [custom, setCustom] = useState(!!value && !known);

  useEffect(() => {
    setCustom(!!value && !targets.some((t) => t.value === value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, targets.length]);

  return (
    <>
      <FormControl size="small" fullWidth>
        <InputLabel>ボタンを押したときの移動先</InputLabel>
        <Select
          label="ボタンを押したときの移動先"
          value={custom ? CUSTOM_LINK : value || ""}
          MenuProps={MENU_PROPS}
          onChange={(e) => {
            if (e.target.value === CUSTOM_LINK) {
              setCustom(true);
              return;
            }
            setCustom(false);
            onChange(e.target.value, targets.find((t) => t.value === e.target.value));
          }}
        >
          {targets.map((t) => (
            <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
          ))}
          <MenuItem value={CUSTOM_LINK}>その他（URLを直接入力）</MenuItem>
        </Select>
      </FormControl>
      {custom && (
        <TextField
          label="移動先のURL" size="small" fullWidth value={value || ""}
          onChange={(e) => onChange(e.target.value, null)}
          helperText="https:// から始まるURLを入力してください（例: https://dance-slash.com/access）。LINE内の予約・入会フォームや会員アプリへは、上の候補から選んでください。"
        />
      )}
    </>
  );
}

/** 実際のLINEに近い見た目でプレビューする（ボタンまで確認できるように）。 */
function LinePreview({ preview }) {
  const msg = preview.message || {};
  let buttons = [];
  try {
    buttons = (msg.contents.footer.contents || [])
      .map((b) => b.action)
      .filter(Boolean)
      .map((a) => ({ label: a.label, uri: a.uri || "（アンケートの回答）" }));
  } catch (e) {
    buttons = [];
  }
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" className="sm-muted">
        送信イメージ（サンプルのお客様情報で差し込んだもの）
      </Typography>
      <div className="sm-line-preview">
        <div className="sm-line-bubble">
          <div className="sm-line-text">{preview.text || "(本文なし)"}</div>
          {buttons.map((b, i) => (
            <div className="sm-line-btn" key={i}>
              <span className="sm-line-btn-label">{b.label}</span>
              <span className="sm-line-btn-uri">{b.uri}</span>
            </div>
          ))}
        </div>
      </div>
    </Box>
  );
}

/**
 * 新規作成。**まず「誰に送るか（お客様の状態）」を選び、次にきっかけ**を選ぶ。
 *
 * ⚠️ 状態(target_tag)は表示・整理用のラベルで、**実際に誰へ届くかはきっかけと条件が決める**。
 * 状態を選んでも配信対象が絞られるわけではないので、画面にもその旨を書いておく。
 */
function CreateDialog({ open, meta, existingIds, onClose, onCreate }) {
  const [status, setStatus] = useState("");
  const [anchor, setAnchor] = useState("");
  const [sid, setSid] = useState("");
  const [name, setName] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setStatus(""); setAnchor(""); setSid(""); setName(""); setShowAll(false);
  }, [open]);

  if (!open) return null;

  const addable = (meta?.anchors || []).filter((a) => a.can_add);
  const suggested = (meta?.status_anchors || {})[status] || [];
  const anchorChoices = showAll || !status
    ? addable
    : addable.filter((a) => suggested.includes(a.value));

  const dup = existingIds.includes(sid.trim());
  const ok = status && anchor && sid.trim() && name.trim() && !dup;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="scenario-manager-page">
      <DialogTitle>メッセージを追加</DialogTitle>
      <DialogContent>
        <SectionTitle>① どのお客様に送りますか</SectionTitle>
        <FormControl size="small" fullWidth sx={{ mb: 1 }}>
          <InputLabel>お客様の状態</InputLabel>
          <Select label="お客様の状態" value={status} MenuProps={MENU_PROPS}
            onChange={(e) => { setStatus(e.target.value); setAnchor(""); setShowAll(false); }}>
            {(meta?.status_tags || []).map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" className="sm-muted">
          ここで選ぶのは<b>管理用の分類</b>です。実際に誰へ届くかは、次の「きっかけ」と作成後の送信条件で決まります。
        </Typography>

        {status && (
          <>
            <SectionTitle>② どのきっかけで送りますか</SectionTitle>
            <FormControl size="small" fullWidth sx={{ mb: 1 }}>
              <InputLabel>きっかけ</InputLabel>
              <Select label="きっかけ" value={anchor} MenuProps={MENU_PROPS}
                onChange={(e) => setAnchor(e.target.value)}>
                {anchorChoices.map((a) => (
                  <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {!showAll && anchorChoices.length < addable.length && (
              <Button size="small" onClick={() => setShowAll(true)}>
                他のきっかけも表示する（{addable.length - anchorChoices.length}件）
              </Button>
            )}
            <Typography variant="caption" className="sm-muted" sx={{ display: "block", mt: 1 }}>
              ※ きっかけは<b>システムが検知できる出来事</b>に限られます。ここに出ているものが全てです。
            </Typography>
          </>
        )}

        {anchor && (
          <>
            <SectionTitle>③ 名前をつけます</SectionTitle>
            <TextField label="管理番号（例: 3-4）" size="small" fullWidth sx={{ mb: 2 }}
              value={sid} onChange={(e) => setSid(e.target.value)}
              error={dup}
              helperText={dup ? "この管理番号はすでに使われています" : "半角で、他と重複しない番号を付けてください"} />
            <TextField label="メッセージの名前（管理用）" size="small" fullWidth
              value={name} onChange={(e) => setName(e.target.value)} />
            <Typography variant="body2" className="sm-muted" sx={{ mt: 2 }}>
              作成したあと、本文とタイミングを入力して保存してください。
              <b>作成直後は「停止中」</b>なので、内容を確認してから「配信する」を ON にしてください。
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>やめる</Button>
        <Button variant="contained" disabled={!ok}
          onClick={() => onCreate({
            scenario_id: sid.trim(), name: name.trim(), anchor,
            target_tag: status, ...newScenarioDefaults(anchor),
          })}>
          作成する
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function SectionTitle({ children }) {
  return (
    <Typography variant="subtitle2" fontWeight={700} className="sm-section-title">
      {children}
    </Typography>
  );
}

function SelectField({ label, value, items, onChange, width = 160 }) {
  // 値が "" の選択肢（「指定しない」）を持つ場合、displayEmpty が無いと MUI は
  // 選択中でも空欄に見えてしまう（実機で判明）。ラベルは常に上に固定する。
  const hasEmptyOption = (items || []).some((it) => it.value === "");
  return (
    <FormControl size="small" sx={{ minWidth: width }}>
      <InputLabel shrink={hasEmptyOption ? true : undefined}>{label}</InputLabel>
      <Select
        label={label}
        value={value ?? ""}
        displayEmpty={hasEmptyOption}
        notched={hasEmptyOption ? true : undefined}
        MenuProps={MENU_PROPS}
        onChange={(e) => onChange(e.target.value)}
      >
        {(items || []).map((it) => (
          <MenuItem key={String(it.value)} value={it.value}>{it.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default ScenarioManagerPage;
